import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./FormCreateLibrary.scss";
import Button from "../../Button/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFormState, useWatch } from "react-hook-form";
import FormSelect, { IOption } from "../FormInput/FormSelect/FormSelect";
import CustomRadio from "../FormInput/CustomRadio/CustomRadio";
import { translations } from "../../../models/SurveyModels";
import {
  RESOURCE_TYPE,
  ROLE_FILTER,
  ResourcePayload,
  initialResource,
  resourceType,
} from "../../../models/LibraryModels";
import Translations, { IPopulated } from "../Translations";
import Skeleton from "react-loading-skeleton";
import { getLocalTranslation } from "../../../utils/functions";

interface IFormLibrary {
  libraryData?: ResourcePayload;
  categories: IOption[];
  helpCategories?: IOption[];
  isLoading: boolean;
  answerType: IOption[];
  onSumbitForm: (data: ResourcePayload) => void;
}

const FormCreateLibrary = ({
  libraryData,
  categories,
  helpCategories = [],
  isLoading,
  answerType,
  onSumbitForm,
}: IFormLibrary) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    unregister,
    clearErrors,
    reset,
  } = useForm<ResourcePayload>({
    mode: "all",
  });
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const options = [
    {
      label: `${t("createLibrary.libraryData.domain.domains.HR")}`,
      value: [ROLE_FILTER.HR],
    },
    {
      label: `${t("createLibrary.libraryData.domain.domains.Manager")}`,
      value: [ROLE_FILTER.HR, ROLE_FILTER.MANAGER],
    },
    {
      label: `${t("createLibrary.libraryData.domain.domains.All")}`,
      value: [ROLE_FILTER.HR, ROLE_FILTER.MANAGER, ROLE_FILTER.EMPLOYEE],
    },
  ];

  const [lib, setLib] = useState<IPopulated>(libraryData || initialResource);

  const formState = useFormState({
    control,
  });
  const controls = useWatch({
    control,
  });
  // Le risorse HELP (Help Center) usano categorie dedicate
  const isHelp = (controls.type ?? libraryData?.type) === RESOURCE_TYPE.HELP;

  const [selectedValue, setSelectedValue] = useState<string[] | string>(
    ROLE_FILTER.HR
  );
  const [translationsForIndex, setTranslations] = useState<IOption[][]>([
    translations,
  ]);
  const [selectedTranslations, setSelectedTranslations] = useState<string[]>(
    []
  );
  const handleAddLanguage = () => {
    const newTranslation = {
      title: "",
      link: "",
      description: "",
      language: "",
    };
    setValue(
      `translations.${controls.translations?.length || 0}`,
      newTranslation
    );
    setLib((oldValue) => ({
      ...oldValue!,
      translations: [...(oldValue?.translations || []), newTranslation],
    }));
    setTranslations((prevTranslations) => [
      ...prevTranslations,
      translations.filter((item) =>
        selectedTranslations.every((term) =>
          Object.values(item).some(
            (value) => !value.toLowerCase().includes(term.toLowerCase())
          )
        )
      ),
    ]);
  };
  const removeLanguage = (index: number) => {
    const newTranslations = [...lib.translations];
    newTranslations.splice(index, 1);
    setLib((oldValue) => ({
      ...oldValue,
      translations: newTranslations,
    }));
    setSelectedTranslations((old) => [...old, ...old.splice(index, 1)]);
    setTranslations((old) => [...old, ...old.splice(index, 1)]);
    unregister(`translations.${index}`);
  };
  const handleRadioChange = useCallback(
    (value: string[]) => {
      setSelectedValue(Array.isArray(value) ? value.join(",") : value);
      setValue(
        "roles",
        !Array.isArray(value)
          ? ((value as string).split(",") as ROLE_FILTER[])
          : (value as ROLE_FILTER[])
      );
    },
    [setValue]
  );

  useEffect(() => {
    if (libraryData) {
      setLib(libraryData);
      libraryData?.translations.forEach(
        ({ language, description, title, link }, index) => {
          setValue(`translations.${index}`, {
            language,
            description,
            title,
            link,
          });
          setSelectedTranslations((old) => {
            if (old.includes(language)) return [...old];
            else return [...old!, language];
          });
        }
      );
      setTranslations((prevTranslations) => {
        const newTranslations = translations.filter((item) =>
          selectedTranslations.every((term) =>
            Object.values(item).some(
              (value) => !value.toLowerCase().includes(term.toLowerCase())
            )
          )
        );
        const isDuplicate = prevTranslations.some((prevItem) => {
          if (prevItem.length !== newTranslations.length) {
            return false;
          }
          return prevItem.every((prevObj, index) => {
            const newObj = newTranslations[index];
            return Object.entries(prevObj).every(
              ([key, value]) => newObj[key] === value
            );
          });
        });

        if (isDuplicate) {
          return prevTranslations;
        } else {
          return [...prevTranslations, newTranslations];
        }
      });
      libraryData?.categoryId &&
        setValue(`categoryId`, libraryData?.categoryId);
      libraryData?.type && setValue(`type`, libraryData?.type);
      libraryData?.roles && handleRadioChange(libraryData?.roles);
    } else {
      const newTranslation = {
        title: "",
        link: "",
        description: "",
        language: "",
      };
      setValue(`translations.${0}`, newTranslation);
      initialResource.roles && handleRadioChange([ROLE_FILTER.HR]);
    }
  }, [handleRadioChange, libraryData, reset, setValue]);

  return (
    <form className="form-create-questions">
      <div className="navbar fixed mb-4">
        <div className="mb-4 container__top">
          <div className="left">
            <h1 className="title-page capitalize-first-letter">
              {isLoading ? (
                <Skeleton />
              ) : libraryData ? (
                getLocalTranslation(libraryData.translations).title
              ) : (
                t("createLibrary.title")
              )}
            </h1>
            <p className="page-description capitalize-first-letter mt-1">
              {t(`createLibrary.description`)}
            </p>
          </div>
          <div className="right">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="tertiary"
                noBorder={true}
                className="capitalize-first-letter"
                onClick={() => navigate(-1)}
              >
                <span>{t("createAgency.labelCancelButton.edit")}</span>
              </Button>
            </div>
            <Button
              onClick={handleSubmit(onSumbitForm)}
              className="capitalize-first-letter"
            >
              <span>{t("createQuestion.labelSubmitButton.create")}</span>
            </Button>
          </div>
        </div>
      </div>
      <div className="form-container">
        <div className="form-content">
          <div className={`col-lg-8 form-content `}>
            <div className="mb-4">
              <h2 className="registry">
                {t("createLibrary.libraryData.domain.title")}
              </h2>
              <p className="page-description">
                {t("createLibrary.libraryData.domain.text")}
              </p>
            </div>
            <h6>{t("createLibrary.libraryData.domain.domains.title")}</h6>
            <div className="ms-3">
              <div className="my-4">
                {isLoading ? (
                  <Skeleton
                    count={3}
                    inline
                    height={30}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <CustomRadio
                    options={options}
                    selectedValue={selectedValue}
                    {...register("roles")}
                    onChange={(value) => {
                      handleRadioChange(value as string[]);
                    }}
                  />
                )}
              </div>
            </div>
            <div className="mt-4">
              <h2 className="registry mb-4">
                {t("createLibrary.libraryData.fields.title")}
              </h2>
              <div className="row position-relative">
                {isLoading ? (
                  <Skeleton
                    count={2}
                    inline
                    height={44}
                    containerClassName="container-radio-skeleton"
                  />
                ) : (
                  <>
                    <FormSelect
                      typeErrors={
                        formState.errors?.categoryId &&
                        (formState.errors?.categoryId.message as string)
                      }
                      isInvalid={formState.errors.hasOwnProperty(`categoryId`)}
                      key={isHelp ? "help-categories" : "categories"}
                      options={isHelp ? helpCategories : categories}
                      wrapperClass="col-lg-6"
                      defaultValue={
                        libraryData?.categoryId && libraryData?.categoryId
                      }
                      required
                      {...register(`categoryId`, {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      label={t(
                        "createLibrary.libraryData.fields.category.label"
                      )}
                      placeholder={t(
                        "createLibrary.libraryData.fields.category.placeholder"
                      )}
                      onChange={(selected) => {
                        clearErrors(`categoryId`);
                        setValue(`categoryId`, selected);
                      }}
                    />
                    <FormSelect
                      typeErrors={
                        formState.errors?.type &&
                        (formState.errors?.type?.message as string)
                      }
                      isInvalid={formState.errors.hasOwnProperty(`type`)}
                      options={resourceType}
                      wrapperClass="col-lg-6"
                      defaultValue={libraryData?.type && libraryData?.type}
                      required
                      label={t(
                        "createLibrary.libraryData.fields.resourceType.label"
                      )}
                      placeholder={t(
                        "createLibrary.libraryData.fields.resourceType.placeholder"
                      )}
                      {...register(`type`, {
                        required: {
                          value: true,
                          message: t("form-generic.error.required"),
                        },
                      })}
                      onChange={(value) => {
                        clearErrors("type");
                        if ((value === RESOURCE_TYPE.HELP) !== isHelp)
                          setValue(`categoryId`, undefined);
                        setValue(`type`, value as RESOURCE_TYPE);
                      }}
                    />
                  </>
                )}

                <div className="col-lg-12 form-content">
                  <h3>{t("createTemplate.title")}</h3>
                  <Translations
                    translationsforIndex={translationsForIndex}
                    handleAddLanguage={handleAddLanguage}
                    removeLanguage={removeLanguage}
                    setSelectedTranslations={setSelectedTranslations}
                    isLoading={isLoading}
                    formState={formState}
                    populated={controls as IPopulated}
                    register={register}
                    unregister={unregister}
                    clearErrors={clearErrors}
                    setValue={setValue}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FormCreateLibrary;
